import { Add } from '@mui/icons-material'
import { ListItem } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { SxProps, Theme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'
import { getEditableFieldInputStyles } from './getEditableFieldInputStyles'

export type Option = { id: string; label: string }

interface EditableDropdownFieldProps {
  value: Option | null
  choices: Option[]
  disabled?: boolean
  readonly?: boolean
  freeSolo?: false
  sx?: SxProps<Theme>
  addNewChoice?: (label: string) => Promise<string | null>
  save?: (newValue: string | null) => Promise<void>
  error?: boolean
  helperText?: string
  disableClearable?: boolean
  onChange?: (newValue: Option | null) => void
  onBlur?: () => void
  isAiPrefilled?: boolean
}

const ADD_NEW_CHOICE_ID = 'add-new-choice'

export const EditableDropdownField: React.FC<EditableDropdownFieldProps> = ({
  value,
  choices,
  addNewChoice,
  disabled = false,
  readonly,
  freeSolo,
  sx,
  save,
  error,
  helperText,
  onChange,
  onBlur,
  disableClearable = true,
  isAiPrefilled = false,
}) => {
  const [currentValue, setCurrentValue] = useState<Option | null>(value)
  const [inputValue, setInputValue] = useState<string>(value?.label || '')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const { t } = useTranslation('custom-fields', { keyPrefix: 'config-dialog' })

  useEffect(() => {
    setCurrentValue(value)
    setInputValue(value?.label || '')
  }, [value])

  if (readonly) {
    return <Typography>{currentValue?.label || ''}</Typography>
  }

  const startEditing = () => {
    if (disabled) return
    setIsEditing(true)
  }

  const handleFinishEditing = async () => {
    if (disabled || readonly) return
    setIsLoading(true)
    let choiceId: string | null = null

    if (currentValue) {
      choiceId = currentValue.id
    } else if (addNewChoice && inputValue) {
      // Handle free solo input
      const newChoiceId = await addNewChoice(inputValue)
      choiceId = newChoiceId
    }

    const isSameValue = value?.id === choiceId
    if (!isSameValue) {
      await save?.(choiceId)
    }
    setIsLoading(false)
    setIsEditing(false)
  }

  return (
    <Box sx={sx} onClick={(e) => e.preventDefault()}>
      <BaseAutocomplete
        disableClearable={disableClearable}
        onFocus={startEditing}
        onClick={startEditing}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        options={[
          ...choices,
          ...(inputValue && addNewChoice && !choices.some((c) => c.label === inputValue)
            ? [{ label: `${inputValue}`, id: ADD_NEW_CHOICE_ID }]
            : []),
        ]}
        value={currentValue}
        inputValue={inputValue}
        onBlur={() => {
          void handleFinishEditing()
          onBlur?.()
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option
          }
          return option.label
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <>
            {option.id === ADD_NEW_CHOICE_ID ? (
              <ListItem
                {...props}
                sx={{
                  background: 'neutralLight.50',
                  borderColor: 'neutralLight.main',
                  '&:hover #add-new-icon': { backgroundColor: 'primary.main', color: 'white' },
                }}
              >
                <Add sx={{ color: 'neutralLight.700', mr: 4 }} />
                <Typography color="primary.main" variant="Body14pxRegular">
                  {t('form.fields.choices.add-new', { option: option.label })}
                </Typography>
              </ListItem>
            ) : (
              <ListItem {...props} key={option.id}>
                {option.label}
              </ListItem>
            )}
          </>
        )}
        disabled={disabled}
        sx={{ ...getEditableFieldInputStyles(isEditing) }}
        renderInput={(params) => {
          if (isLoading) {
            params.InputProps.endAdornment = <CircularProgress size={13} color="neutralDark" />
          }

          if (!isEditing && !isHovering) {
            params.InputProps.endAdornment = null
          }

          return (
            <div>
              <TextField
                {...params}
                placeholder="Select..."
                onKeyDown={(event) => {
                  switch (event.key) {
                    case 'Enter':
                      return handleFinishEditing()
                    case 'Escape':
                      return setIsEditing(false)
                  }
                }}
                error={error}
                helperText={helperText}
                data-is-ai-prefilled={isAiPrefilled}
              />
            </div>
          )
        }}
        onChange={async (_, value) => {
          if (value && value.id === ADD_NEW_CHOICE_ID && addNewChoice) {
            const newChoiceId = await addNewChoice(value.label)
            if (newChoiceId) {
              setCurrentValue({ id: newChoiceId, label: value.label })
              setInputValue(value.label)
              return
            }
          }

          let picked: Option | null = null
          if (typeof value === 'string') {
            setCurrentValue(picked)
            setInputValue(value)
          } else {
            setCurrentValue(value as Option | null)
            picked = value as Option | null
            setCurrentValue(picked)
          }

          onChange?.(picked)
        }}
        freeSolo={freeSolo}
        multiple={false}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue)
          if (freeSolo) {
            setCurrentValue(null) // Clear the selected option when typing in free solo mode
          }
        }}
      />
    </Box>
  )
}
